import { ref, computed, watch } from "vue";
import { submitFeedback } from "./usePinningSession";

const SUCCESS_COLLAPSE_DELAY_MS = 1500;

/**
 * Composable for managing pinning session feedback (thumbs up/down).
 *
 * @param {import('vue').Ref<string | null>} sessionIdRef - Reactive ref to the current session ID
 * @param {import('vue').Ref<Array>} pinsRef - Reactive ref to the pins array
 * @param {import('vue').Ref<string | null>} lastEditedPinIdRef - Reactive ref to the last edited pin ID
 * @returns {object} Feedback state and handlers
 */
// eslint-disable-next-line import/prefer-default-export, max-lines-per-function, max-params
export function usePinningFeedback(sessionIdRef, pinsRef, lastEditedPinIdRef, { toast } = {}) {
  const positive = ref(null);
  const message = ref("");
  const isOpen = ref(false);
  const status = ref("idle");

  let successTimeoutId = null;

  const lastEditedPin = computed(() => {
    if (!lastEditedPinIdRef.value) return null;
    return pinsRef.value.find(pin => pin.id === lastEditedPinIdRef.value) ?? null;
  });

  function resetFeedback() {
    positive.value = null;
    message.value = "";
    isOpen.value = false;
    status.value = "idle";
  }

  function handleSelectPositive() {
    positive.value = true;
    isOpen.value = true;
    status.value = "idle";
  }

  function handleSelectNegative() {
    positive.value = false;
    isOpen.value = true;
    status.value = "idle";
  }

  function handleCancel() {
    resetFeedback();
  }

  async function handleSubmit() {
    if (positive.value === null) return;

    const sessionId = sessionIdRef.value;
    if (!sessionId) {
      toast?.error({
        title: "Unable to submit feedback",
        description: "Missing session information."
      });
      return;
    }

    status.value = "sending";

    try {
      await submitFeedback(sessionId, {
        positive: positive.value,
        message: message.value.trim() || null,
        currentPin: lastEditedPin.value?.id ?? null,
        currentPinState: lastEditedPin.value?.state ?? null
      });
      status.value = "success";
    } catch {
      status.value = "error";
      toast?.error({
        title: "Feedback failed",
        description: "Something went wrong. Please try again."
      });
    }
  }

  watch(status, newStatus => {
    if (newStatus !== "success") return;

    if (successTimeoutId) {
      clearTimeout(successTimeoutId);
    }

    successTimeoutId = setTimeout(() => {
      resetFeedback();
    }, SUCCESS_COLLAPSE_DELAY_MS);
  });

  function cleanup() {
    if (successTimeoutId) {
      clearTimeout(successTimeoutId);
      successTimeoutId = null;
    }
  }

  return {
    positive,
    message,
    isOpen,
    status,
    handleSelectPositive,
    handleSelectNegative,
    handleCancel,
    handleSubmit,
    resetFeedback,
    cleanup
  };
}
